/**
 * SCforH-aligned SMART packs for Health-motivated table tennis players.
 * Compose S·M·A·R·T from the player health seed (WHO × dimension × intensity).
 * Must stay in sync with Backend/src/config/healthSmartPresets.js
 */
import { PURPOSE_HEALTH } from "./profilingFocusCatalog";
import {
  DEFAULT_HEALTH_PHASE_WEEKS,
  DEFAULT_HEALTH_SESSION_HOURS,
  isHealthPurpose,
  normalizeHealthPlayerSeed,
  seedFromHealthSlots,
} from "./healthPlayerProfiling";

export { isHealthPurpose };

function langKey(lang) {
  const raw = String(lang || "en").toLowerCase().split("-")[0];
  if (raw === "gr" || raw === "ge") return "de";
  if (raw === "sp") return "es";
  if (["en", "fr", "es", "de", "nl", "pt"].includes(raw)) return raw;
  return "en";
}

function pick(obj, lk) {
  if (!obj) return "";
  if (typeof obj === "string") return obj;
  return obj[lk] || obj.en || "";
}

const WHO_MINUTES = {
  children: { en: "60 minutes of activity a day", fr: "60 minutes d'activité par jour", es: "60 minutos de actividad al día", de: "60 Minuten Bewegung pro Tag", nl: "60 minuten activiteit per dag", pt: "60 minutos de atividade por dia" },
  adolescents: { en: "60 minutes of activity a day", fr: "60 minutes d'activité par jour", es: "60 minutos de actividad al día", de: "60 Minuten Bewegung pro Tag", nl: "60 minuten activiteit per dag", pt: "60 minutos de atividade por dia" },
  adults: { en: "150 minutes of moderate activity a week (or 75 vigorous) plus 2 strength sessions", fr: "150 minutes d'activité modérée par semaine (ou 75 vigoureuses) plus 2 séances de renforcement", es: "150 minutos de actividad moderada por semana (o 75 vigorosa) más 2 sesiones de fuerza", de: "150 Minuten moderate Aktivität pro Woche (oder 75 intensive) plus 2 Kraft-Einheiten", nl: "150 minuten matige activiteit per week (of 75 intensief) plus 2 krachtrainingen", pt: "150 minutos de atividade moderada por semana (ou 75 vigorosa) mais 2 sessões de força" },
  seniors: { en: "150 minutes a week plus 2 strength sessions; add balance 3×/week if mobility is limited", fr: "150 minutes par semaine plus 2 séances de renforcement ; ajouter l'équilibre 3×/semaine si la mobilité est limitée", es: "150 minutos por semana más 2 sesiones de fuerza; añade equilibrio 3×/semana si la movilidad es limitada", de: "150 Minuten pro Woche plus 2 Kraft-Einheiten; bei eingeschränkter Mobilität 3×/Woche Balance", nl: "150 minuten per week plus 2 krachtrainingen; voeg 3×/week balans toe bij beperkte mobiliteit", pt: "150 minutos por semana mais 2 sessões de força; acrescentar equilíbrio 3×/semana se a mobilidade for limitada" },
};

const DIM_GROW = {
  physical: {
    en: "physical fitness through club table tennis (rallies, footwork, movement)",
    fr: "la forme physique par le ping au club (échanges, déplacements)",
    es: "la forma física con el ping del club (peloteos, desplazamiento)",
    de: "körperliche Fitness durch Vereinstischtennis (Ballwechsel, Beinarbeit)",
    nl: "fysieke fitheid via clubtafeltennis (rally's, voetwerk)",
    pt: "a forma física através do ténis de mesa no clube (trocas, deslocações)",
  },
  mental: {
    en: "mental well-being through focused, enjoyable rallies",
    fr: "le bien-être mental par des échanges concentrés et plaisants",
    es: "el bienestar mental con peloteos concentrados y agradables",
    de: "mentales Wohlbefinden durch konzentrierte, freudvolle Ballwechsel",
    nl: "mentaal welzijn via geconcentreerde, plezierige rally's",
    pt: "o bem-estar mental com trocas concentradas e prazerosas",
  },
  social: {
    en: "belonging through doubles and new partners in existing groups",
    fr: "l'appartenance via le double et de nouveaux partenaires dans les groupes existants",
    es: "la pertenencia con dobles y nuevos compañeros en los grupos actuales",
    de: "Zugehörigkeit durch Doppel und neue Partner in bestehenden Gruppen",
    nl: "verbondenheid via dubbelspel en nieuwe partners in bestaande groepen",
    pt: "o sentimento de pertença com pares e novos parceiros nos grupos atuais",
  },
};

const FOCUS_HINT = {
  1: { en: "learning the basic strokes", fr: "apprendre les coups de base", es: "aprender los golpes básicos", de: "die Grundschläge lernen", nl: "de basisslagen leren", pt: "aprender os golpes básicos" },
  2: { en: "hand-eye coordination", fr: "la coordination œil-main", es: "la coordinación ojo-mano", de: "Hand-Auge-Koordination", nl: "oog-handcoördinatie", pt: "a coordenação olho-mão" },
  4: { en: "playing with others", fr: "jouer avec les autres", es: "jugar con otras personas", de: "mit anderen spielen", nl: "met anderen spelen", pt: "jogar com outras pessoas" },
  5: { en: "staying active and fit", fr: "rester actif et en forme", es: "mantenerse activo y en forma", de: "aktiv und fit bleiben", nl: "actief en fit blijven", pt: "manter-me ativo e em forma" },
  6: { en: "keeping the mind sharp", fr: "garder l'esprit vif", es: "mantener la mente despierta", de: "den Kopf wach halten", nl: "het hoofd scherp houden", pt: "manter a mente alerta" },
  19: { en: "unwinding after the day", fr: "décompresser après la journée", es: "desconectar después del día", de: "nach dem Tag abschalten", nl: "ontspannen na de dag", pt: "descontrair depois do dia" },
  20: { en: "gentle return to movement", fr: "un retour en douceur au mouvement", es: "un retorno suave al movimiento", de: "sanfte Rückkehr zur Bewegung", nl: "zachte terugkeer naar bewegen", pt: "um regresso suave ao movimento" },
  21: { en: "low-impact club play", fr: "un jeu club à faible impact", es: "juego de club de bajo impacto", de: "gelenksames Vereinsspiel", nl: "clubspel met weinig impact", pt: "jogo de clube de baixo impacto" },
  22: { en: "adapted, coach-aware play", fr: "un jeu adapté, connu de l'entraîneur", es: "juego adaptado, conocido por el entrenador", de: "angepasstes Spiel, das der Trainer kennt", nl: "aangepast spel dat de coach kent", pt: "jogo adaptado, conhecido pelo treinador" },
};

/** Letter A — training milestones support physical & mental objectives (no intensity question). */
const MILESTONES_SUPPORT_A = {
  en: "You will have training milestones that support your physical and mental objectives.",
  fr: "Vous aurez des jalons d'entraînement qui soutiennent vos objectifs physiques et mentaux.",
  es: "Tendrás hitos de entrenamiento que apoyan tus objetivos físicos y mentales.",
  de: "Du erhältst Trainingsmeilensteine, die deine körperlichen und mentalen Ziele unterstützen.",
  nl: "Je krijgt trainingsmijlpalen die je fysieke en mentale doelen ondersteunen.",
  pt: "Terás marcos de treino que apoiam os teus objetivos físicos e mentais.",
};

/** Letter R — coach ensures relevance (no delivery-mode question). */
const COACH_RELEVANT_R = {
  en: "Your coach will make sure the plan is relevant for your objectives.",
  fr: "Votre coach veillera à ce que le plan soit pertinent pour vos objectifs.",
  es: "Tu coach se asegurará de que el plan sea relevante para tus objetivos.",
  de: "Dein Coach stellt sicher, dass der Plan für deine Ziele relevant ist.",
  nl: "Je coach zorgt dat het plan relevant is voor jouw doelen.",
  pt: "O teu treinador garante que o plano é relevante para os teus objetivos.",
};

const QUEST = {
  physical: { en: "Club fitness", fr: "Forme au club", es: "Forma en el club", de: "Vereinsfitness", nl: "Clubfitheid", pt: "Forma no clube" },
  mental: { en: "Clear head", fr: "Tête claire", es: "Mente clara", de: "Klarer Kopf", nl: "Helder hoofd", pt: "Cabeça clara" },
  social: { en: "Club belonging", fr: "Appartenance club", es: "Pertenencia al club", de: "Vereinszugehörigkeit", nl: "Clubverbondenheid", pt: "Pertença ao clube" },
};

const MILESTONES = {
  physical: {
    en: "Keep {sessions} club session(s) a week for 4 months; feel fitter in rallies",
    fr: "Tenir {sessions} séance(s) club par semaine pendant 4 mois ; se sentir plus en forme en échange",
    es: "Mantener {sessions} sesión(es) de club por semana durante 4 meses; sentirme más en forma en los peloteos",
    de: "{sessions} Vereinseinheit(en) pro Woche über 4 Monate halten; fitter in Ballwechseln",
    nl: "{sessions} clubsessie(s) per week 4 maanden volhouden; fitter in rally's",
    pt: "Manter {sessions} sessão(ões) de clube por semana durante 4 meses; sentir-me mais em forma nas trocas",
  },
  mental: {
    en: "After each session, rate ease 1–5; keep three months of enjoyable play",
    fr: "Après chaque séance, noter le ressenti 1–5 ; tenir trois mois de jeu plaisant",
    es: "Tras cada sesión, nota de 1–5; mantener tres meses de juego agradable",
    de: "Nach jeder Einheit Wohlbefinden 1–5; drei Monate freudvolles Spiel halten",
    nl: "Na elke sessie score 1–5; drie maanden prettig spel volhouden",
    pt: "Após cada sessão, nota 1–5; manter três meses de jogo prazeroso",
  },
  social: {
    en: "Play with someone new at the club this month, and keep weekly sessions",
    fr: "Jouer avec quelqu'un de nouveau au club ce mois-ci, et tenir les séances hebdo",
    es: "Jugar con alguien nuevo en el club este mes, y mantener las sesiones semanales",
    de: "Diesen Monat mit jemandem Neuem spielen und wöchentliche Einheiten halten",
    nl: "Deze maand met iemand nieuws spelen en wekelijkse sessies volhouden",
    pt: "Jogar com alguém novo no clube este mês e manter as sessões semanais",
  },
};

function sessionsLabel(sessionsPerWeek) {
  if (sessionsPerWeek === "3+") return "3+";
  if (sessionsPerWeek === "2") return "2";
  return "1";
}

function leadDimension(dims) {
  if (dims.includes("mental") && !dims.includes("physical")) return "mental";
  if (dims.includes("social") && dims.length === 1) return "social";
  if (dims.includes("physical")) return "physical";
  return dims[0] || "physical";
}

export function resolveHealthSmartPreset({ seed, lang } = {}) {
  const lk = langKey(lang);
  const s = normalizeHealthPlayerSeed(seed);
  const dims = s.healthDimensions.length ? s.healthDimensions : ["physical"];
  const lead = leadDimension(dims);
  const sessions = sessionsLabel(s.sessionsPerWeek);
  const who = pick(WHO_MINUTES[s.whoBand] || WHO_MINUTES.adults, lk);
  const growBits = dims.map((d) => pick(DIM_GROW[d], lk)).filter(Boolean);
  const focusHint = pick(FOCUS_HINT[s.primaryFocusId] || FOCUS_HINT[5], lk);
  const gap =
    s.whoSelfCheck === "below"
      ? pick(
          {
            en: "I am below the guideline today — club sessions help close the gap.",
            fr: "Je suis sous la reco aujourd'hui — les séances club aident à rattraper.",
            es: "Hoy estoy por debajo de la pauta — las sesiones del club ayudan a cerrar la brecha.",
            de: "Ich liege heute unter der Empfehlung — Vereinseinheiten schließen die Lücke.",
            nl: "Ik zit nu onder de richtlijn — clubsessies helpen de kloof te dichten.",
            pt: "Estou abaixo da diretriz hoje — as sessões do clube ajudam a fechar o fosso.",
          },
          lk
        )
      : s.whoSelfCheck === "above"
        ? pick(
            {
              en: "I already meet the guideline — club TT keeps it going.",
              fr: "J'atteins déjà la reco — le ping club l'entretient.",
              es: "Ya cumplo la pauta — el ping del club lo mantiene.",
              de: "Ich erfülle die Empfehlung bereits — Vereinstischtennis hält sie.",
              nl: "Ik haal de richtlijn al — club-TT houdt het vol.",
              pt: "Já cumpro a diretriz — o ténis de mesa do clube mantém-na.",
            },
            lk
          )
        : pick(
            {
              en: "I am around the guideline — club sessions keep me there.",
              fr: "Je suis autour de la reco — les séances club m'y maintiennent.",
              es: "Estoy alrededor de la pauta — las sesiones del club me mantienen.",
              de: "Ich liege um die Empfehlung — Vereinseinheiten halten mich dort.",
              nl: "Ik zit rond de richtlijn — clubsessies houden me daar.",
              pt: "Estou perto da diretriz — as sessões do clube mantêm-me lá.",
            },
            lk
          );

  const sSummary = pick(
    {
      en: `Grow ${growBits.join(" and ")}, with a focus on ${focusHint}.`,
      fr: `Faire grandir ${growBits.join(" et ")}, en me concentrant sur ${focusHint}.`,
      es: `Hacer crecer ${growBits.join(" y ")}, con foco en ${focusHint}.`,
      de: `${growBits.join(" und ")} stärken, mit Fokus auf ${focusHint}.`,
      nl: `${growBits.join(" en ")} laten groeien, met focus op ${focusHint}.`,
      pt: `Fazer crescer ${growBits.join(" e ")}, com foco em ${focusHint}.`,
    },
    lk
  );

  const mLabel = pick(
    {
      en: `${sessions} club session(s)/week toward ${who}. Club TT counts toward the total, not a TT-only quota. ${gap}`,
      fr: `${sessions} séance(s) club/semaine vers ${who}. Le ping club compte pour le total, ce n'est pas un quota ping seul. ${gap}`,
      es: `${sessions} sesión(es) de club/semana hacia ${who}. El ping del club cuenta para el total, no es un cupo solo de ping. ${gap}`,
      de: `${sessions} Vereinseinheit(en)/Woche in Richtung ${who}. Vereinstischtennis zählt zum Total, keine TT-only-Quote. ${gap}`,
      nl: `${sessions} clubsessie(s)/week richting ${who}. Club-TT telt mee voor het totaal, geen TT-only quota. ${gap}`,
      pt: `${sessions} sessão(ões) de clube/semana rumo a ${who}. O ténis de mesa do clube conta para o total, não é um quota só de ping. ${gap}`,
    },
    lk
  );

  const aLabel = pick(MILESTONES_SUPPORT_A, lk);
  const rLabel = pick(COACH_RELEVANT_R, lk);
  const milestone = pick(MILESTONES[lead] || MILESTONES.physical, lk).replace("{sessions}", sessions);
  const tLabel = pick(
    {
      en: "4-month / season phase, then review with the club",
      fr: "Phase de 4 mois / saison, puis bilan avec le club",
      es: "Fase de 4 meses / temporada, luego revisión con el club",
      de: "4-Monats- / Saisonphase, dann Review mit dem Verein",
      nl: "4-maanden- / seizoensfase, daarna nabespreking met de club",
      pt: "Fase de 4 meses / época, depois revisão com o clube",
    },
    lk
  );

  return {
    primaryFocusId: s.primaryFocusId,
    experienceLevelId: s.experienceLevelId,
    questTitle: pick(QUEST[lead] || QUEST.physical, lk),
    S: { summary: sSummary, label: sSummary },
    M: {
      metricKind: "who_sessions",
      label: mLabel,
      targetHint: mLabel,
    },
    A: {
      milestoneId: `health_${lead}`,
      horizon: "mid",
      label: milestone,
      intensityNote: aLabel,
    },
    R: {
      whyItMatters: rLabel,
      chipId: "health",
    },
    T: {
      defaultCycleWeeks: DEFAULT_HEALTH_PHASE_WEEKS,
      reviewHint: tLabel,
    },
    suggestedExerciseTags: ["health", lead, s.intensity || "regular"],
  };
}

export function applyHealthPresetToSlots(slots, { seed, lang } = {}) {
  const resolvedSeed = seed || seedFromHealthSlots(slots);
  const preset = resolveHealthSmartPreset({ seed: resolvedSeed, lang });
  const next = {
    S: { ...(slots?.S || {}), labels: { ...(slots?.S?.labels || {}) } },
    M: { ...(slots?.M || {}), labels: { ...(slots?.M?.labels || {}) } },
    A: { ...(slots?.A || {}), labels: { ...(slots?.A?.labels || {}) } },
    R: { ...(slots?.R || {}), labels: { ...(slots?.R?.labels || {}) } },
    T: { ...(slots?.T || {}), labels: { ...(slots?.T?.labels || {}) } },
    health: { ...(slots?.health || {}), ...resolvedSeed },
  };

  next.S.purposeId = PURPOSE_HEALTH;
  next.S.labels.purpose = next.S.labels.purpose || "Health";
  if (preset.S.summary) {
    next.S.labels.healthSpecific = preset.S.summary;
  }
  next.A.milestones = [
    {
      id: preset.A.milestoneId,
      horizon: preset.A.horizon,
      label: preset.A.label,
    },
  ];
  next.A.labels.milestones = preset.A.label;
  next.A.resourceNote = preset.A.intensityNote || "";
  next.R.whyItMatters = preset.R.whyItMatters;
  next.R.alignedPurposeId = PURPOSE_HEALTH;
  next.R.motivationSummary = preset.R.whyItMatters;
  next.R.labels.why = preset.R.whyItMatters;
  next.T.cycleWeeks = preset.T.defaultCycleWeeks;
  next.T.reviewLastWeek = true;
  if (!next.T.cycleStart) {
    next.T.cycleStart = new Date().toISOString().slice(0, 10);
  }
  next.T.labels.cycle = preset.T.reviewHint;
  next.M.metric = {
    kind: preset.M.metricKind,
    label: preset.M.label,
    targetHint: preset.M.targetHint,
  };
  next.M.labels.metric = preset.M.label;
  if (!next.M.weeklyTraining?.sessionsPerWeek && resolvedSeed.sessionsPerWeek) {
    next.M.weeklyTraining = {
      sessionsPerWeek: resolvedSeed.sessionsPerWeek,
      hoursPerSession: DEFAULT_HEALTH_SESSION_HOURS,
      hoursPerWeek: null,
    };
  }

  return { slots: next, preset };
}

export const HEALTH_EXPERIENCE_LABELS = {
  en: { 1: "New to table tennis", 2: "I've played a bit", 3: "I play regularly" },
  fr: { 1: "Nouveau au tennis de table", 2: "J'ai déjà un peu joué", 3: "Je joue régulièrement" },
  es: { 1: "Nuevo en el tenis de mesa", 2: "Ya he jugado un poco", 3: "Juego con regularidad" },
  de: { 1: "Neu im Tischtennis", 2: "Ich habe schon ein bisschen gespielt", 3: "Ich spiele regelmäßig" },
  nl: { 1: "Nieuw in tafeltennis", 2: "Ik heb al een beetje gespeeld", 3: "Ik speel regelmatig" },
  pt: { 1: "Novo no ténis de mesa", 2: "Já joguei um pouco", 3: "Jogo com regularidade" },
};

export function healthExperienceLabel(experienceLevelId, lang) {
  const lk = langKey(lang);
  const id = Number(experienceLevelId);
  return HEALTH_EXPERIENCE_LABELS[lk]?.[id] || HEALTH_EXPERIENCE_LABELS.en[id] || "";
}
